import Button from '@/components/UI/Button';
import FormRender from '@/components/UI/FormRender';
import ApprovalSection from '@/components/Studies/Show/ApprovalSection';
import { _isNil, _notNil } from '@/littledash';
import type { RequestTemplateGroups } from '@/model/Request.model';
import { useEffect, useMemo, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form-latest';

type ApprovalFormData = Record<string, unknown>;

type ApprovalFormDefaultValue = ApprovalFormData | Array<Partial<ApprovalFormData>>;

const normalizeDefaultValue = (value?: ApprovalFormDefaultValue): ApprovalFormData | undefined => {
  if (_isNil(value)) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return Object.assign({}, ...value) as ApprovalFormData;
  }
  return value;
};

interface ApprovalFormProps {
  onSubmit: (data: ApprovalFormData) => void;
  groups: Array<RequestTemplateGroups>;
  defaultValue?: ApprovalFormDefaultValue;
  disabled?: boolean;
  isSubmitting?: boolean;
  isSubmitted?: boolean;
}

const ApprovalForm: FC<ApprovalFormProps> = ({
  onSubmit,
  groups,
  defaultValue,
  disabled = false,
  isSubmitting = false,
  isSubmitted = false,
}) => {
  const normalizedDefaultValue = useMemo(() => normalizeDefaultValue(defaultValue), [defaultValue]);
  const formMethods = useForm<ApprovalFormData>({ defaultValues: normalizedDefaultValue });
  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    if (isSubmitted && _notNil(normalizedDefaultValue)) {
      reset(normalizedDefaultValue);
    }
  }, [isSubmitted, normalizedDefaultValue, reset]);

  return (
    <FormProvider {...formMethods}>
      <form data-test-component="ApprovalForm" data-test-element="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormRender
          groupContainer={ApprovalSection}
          groups={groups}
          disabled={disabled}
          submitted={isSubmitted}
          hookFormVersion="latest"
        />
        {!isSubmitted && (
          <Button className="mt3" submit testId="approval-form-submit" disabled={disabled || isSubmitting}>
            {isSubmitting ? 'Submitting' : 'Submit'}
          </Button>
        )}
      </form>
    </FormProvider>
  );
};

export default ApprovalForm;
